/**
 * Willa Krause storefront referral capture
 * Contract: wk-referral-codes-guide-2026-10-07
 * - Query keys (first match): ref, referral, referral_code
 * - Path: /ref=CODE or /ref/CODE
 * - Normalize: uppercase A–Z0–9 only, max 20
 * - Persist 90 days: cookie wk_ref (Path=/, SameSite=Lax) + localStorage wk_ref
 * - URL wins over cookie/localStorage
 * - POST /cart/update.js attributes { ref, referral_code }
 * Fail-soft: never throw uncaught errors that break cart/checkout.
 * v2 (WK audit 09/10/2026, approved by Zander): verify the cart really holds
 *   the code (1.5 s, 4 s, 9 s and after add/change) and re-post.
 * v3 (affiliate portal 09/10/2026, approved by Zander): when a visitor lands on
 *   a ?ref= link, send one small "link opened" beacon to WK Online so the
 *   affiliate dashboard can count clicks. Once per code per 24 h per browser,
 *   random visitor id only (no personal data), sent after capture is done and
 *   fully fail-soft: if WK Online is down nothing else changes.
 */
(function () {
  "use strict";

  var COOKIE = "wk_ref";
  var DAYS = 90;
  var MAX = 20;
  var QUERY_KEYS = ["ref", "referral", "referral_code"];
  var HIT_URL = "https://willakrauseskincare.com/api/ref-hit";
  var VID_KEY = "wk_vid";
  var HIT_KEY = "wk_ref_hit";
  var DAY_MS = 24 * 60 * 60 * 1000;

  function normalize(raw) {
    if (raw == null) return "";
    try {
      return String(raw)
        .toUpperCase()
        .replace(/[^A-Z0-9]/g, "")
        .slice(0, MAX);
    } catch (e) {
      return "";
    }
  }

  function readQuery() {
    try {
      var params = new URLSearchParams(window.location.search || "");
      for (var i = 0; i < QUERY_KEYS.length; i++) {
        var v = params.get(QUERY_KEYS[i]);
        var code = normalize(v);
        if (code) return code;
      }
    } catch (e) {}
    return "";
  }

  function readPath() {
    try {
      var path = window.location.pathname || "";
      var m = path.match(/\/ref[=/]([A-Za-z0-9_-]+)/i);
      if (m && m[1]) return normalize(m[1]);
    } catch (e) {}
    return "";
  }

  function readCookie() {
    try {
      var parts = (document.cookie || "").split(";");
      for (var i = 0; i < parts.length; i++) {
        var p = parts[i].replace(/^\s+|\s+$/g, "");
        if (p.indexOf(COOKIE + "=") === 0) {
          return normalize(decodeURIComponent(p.slice(COOKIE.length + 1)));
        }
      }
    } catch (e) {}
    return "";
  }

  function readLocal() {
    try {
      if (!window.localStorage) return "";
      return normalize(window.localStorage.getItem(COOKIE));
    } catch (e) {
      return "";
    }
  }

  function writeCookie(code) {
    try {
      var maxAge = DAYS * 24 * 60 * 60;
      var secure =
        window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie =
        COOKIE +
        "=" +
        encodeURIComponent(code) +
        "; Max-Age=" +
        maxAge +
        "; Path=/; SameSite=Lax" +
        secure;
    } catch (e) {}
  }

  function writeLocal(code) {
    try {
      if (window.localStorage) window.localStorage.setItem(COOKIE, code);
    } catch (e) {}
  }

  function persist(code) {
    if (!code) return;
    writeCookie(code);
    writeLocal(code);
  }

  function resolveCode() {
    var fromUrl = readQuery() || readPath();
    if (fromUrl) {
      persist(fromUrl);
      return fromUrl;
    }
    var fromCookie = readCookie();
    if (fromCookie) {
      // Refresh cookie expiry + mirror to localStorage
      persist(fromCookie);
      return fromCookie;
    }
    var fromLocal = readLocal();
    if (fromLocal) {
      persist(fromLocal);
      return fromLocal;
    }
    return "";
  }

  function cartHasCode(code) {
    return fetch("/cart.js", { credentials: "same-origin", cache: "no-store" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var a = (cart && cart.attributes) || {};
        return normalize(a.ref) === code;
      })
      .catch(function () { return true; }); // fail-soft: don't loop on errors
  }

  function postCartAttributes(code) {
    if (!code) return Promise.resolve();
    try {
      var body = JSON.stringify({
        attributes: { ref: code, referral_code: code },
      });
      if (typeof fetch === "function") {
        return fetch("/cart/update.js", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: body,
          credentials: "same-origin",
          keepalive: true,
        }).catch(function () {});
      }
      var xhr = new XMLHttpRequest();
      xhr.open("POST", "/cart/update.js", true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.setRequestHeader("Accept", "application/json");
      xhr.send(body);
    } catch (e) {}
    return Promise.resolve();
  }

  // v2 (audit 09/10/2026): on a brand-new cart, another app's /cart/update.js
  // (Monster Cart) can create a second cart at the same moment and win the
  // cart cookie, so our attributes end up on a cart that is thrown away.
  // Re-check the live cart a few times and after any add-to-cart, and
  // re-post when the code is missing.
  var CHECKS_MS = [1500, 4000, 9000];
  var busy = false;
  function ensure(code) {
    if (!code || busy || typeof fetch !== "function") return;
    busy = true;
    cartHasCode(code)
      .then(function (ok) { return ok ? null : postCartAttributes(code); })
      .then(function () { busy = false; }, function () { busy = false; });
  }

  function watchCartAdds(code) {
    try {
      var isCartWrite = function (u) {
        u = String(u || "").split("#")[0].split("?")[0].replace(/^https?:\/\/[^\/]+/, "");
        return /\/cart\/(add|change|clear)(\.js)?$/.test(u);
      };
      if (window.fetch) {
        var of = window.fetch;
        window.fetch = function (input) {
          var u = typeof input === "string" ? input : input && input.url;
          var p = of.apply(this, arguments);
          if (isCartWrite(u)) p.then(function () { setTimeout(function () { ensure(code); }, 300); }, function () {});
          return p;
        };
      }
      var oo = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (m, u) {
        if (isCartWrite(u)) this.addEventListener("loadend", function () { setTimeout(function () { ensure(code); }, 300); });
        return oo.apply(this, arguments);
      };
    } catch (e) {}
  }

  // v3: click beacon (never blocks or changes capture).
  function visitorId() {
    try {
      var v = window.localStorage.getItem(VID_KEY) || "";
      if (!/^[A-Za-z0-9_-]{8,64}$/.test(v)) {
        var chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
        var bytes = new Uint8Array(22);
        window.crypto.getRandomValues(bytes);
        v = "";
        for (var i = 0; i < bytes.length; i++) v += chars.charAt(bytes[i] % chars.length);
        window.localStorage.setItem(VID_KEY, v);
      }
      return v;
    } catch (e) {
      return "";
    }
  }

  function seenToday(code) {
    try {
      var now = Date.now();
      var map = JSON.parse(window.localStorage.getItem(HIT_KEY) || "{}") || {};
      for (var k in map) {
        if (Object.prototype.hasOwnProperty.call(map, k) && now - map[k] > DAY_MS) delete map[k];
      }
      if (map[code] && now - map[code] < DAY_MS) return true;
      map[code] = now;
      window.localStorage.setItem(HIT_KEY, JSON.stringify(map));
    } catch (e) {}
    return false;
  }

  function sendHit(code) {
    try {
      if (!code || seenToday(code)) return;
      var body = JSON.stringify({ code: code, v: visitorId() });
      var sent = false;
      if (navigator.sendBeacon && window.Blob) {
        sent = navigator.sendBeacon(HIT_URL, new Blob([body], { type: "text/plain" }));
      }
      if (!sent && typeof fetch === "function") {
        fetch(HIT_URL, {
          method: "POST",
          mode: "no-cors",
          credentials: "omit",
          keepalive: true,
          headers: { "Content-Type": "text/plain" },
          body: body,
        }).catch(function () {});
      }
    } catch (e) {}
  }

  function run() {
    try {
      var landed = readQuery() || readPath();
      var code = resolveCode();
      if (!code) return;
      postCartAttributes(code);
      for (var i = 0; i < CHECKS_MS.length; i++) {
        setTimeout(function () { ensure(code); }, CHECKS_MS[i]);
      }
      watchCartAdds(code);
      if (landed) setTimeout(function () { sendHit(landed); }, 2000);
    } catch (e) {}
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", run);
  } else {
    run();
  }
})();
